import type { RouteRecordRaw } from 'vue-router';
import { Authenticated, ModelBindings, Require2FA, ServerInstalled } from '~/middlewares';
import { useModelsStore } from '~/stores';
import { about } from '~/wayfinder/routes/admin/management/servers/manage';

export const serverRoutes: RouteRecordRaw[] = [
    // Server blocked route - separate from main server routes to avoid ModelBindings
    {
        path: '/server/:server/blocked',
        name: 'server.blocked',
        component: () => import('~/views/errors/ServerError.vue'),
        meta: {
            middlewares: [Authenticated],
        },
    },
    {
        path: '/server/:server',
        component: () => import('~/views/GenericLayout.vue'),
        meta: {
            middlewares: [Authenticated, Require2FA, new ModelBindings('client'), ServerInstalled],
            showChildrenInNavbar: true,
        },
        children: [
            {
                name: 'server.system.index',
                path: '',
                component: () => import('~/views/server/Index.vue'),
                meta: {
                    icon: 'server',
                },
            },
            {
                name: 'server.rust.live-map',
                path: 'live-map',
                component: () => import('~/views/server/rust/pages/RustGodTools.vue'),
                props: { page: 'map' },
                meta: {
                    addon: 'physgun',
                    eggTag: 'rust',
                    icon: 'map',
                    permission: 'rust_live_map.access',
                    stretch: true,
                    noBreadcrumbs: true,
                    disableNumberFlow: true,
                    fullscreen: true,
                    noPadding: true,
                },
            },
            {
                name: 'server.rust.players',
                path: 'players',
                component: () => import('~/views/server/rust/pages/RustGodTools.vue'),
                props: { page: 'players' },
                meta: {
                    addon: 'physgun',
                    eggTag: 'rust',
                    icon: 'users',
                    permission: 'control.command',
                    stretch: true,
                    noBreadcrumbs: true,
                    noPadding: true,
                },
            },
            {
                name: 'server.tools.grafana',
                path: 'metrics',
                component: () => import('~/views/server/grafana/Index.vue'),
                meta: {
                    icon: 'chart-line',
                    permission: 'grafana.read',
                    addon: 'physgun',
                    eggTag: ['rust', 'garrysmod', 'sbox'],
                    stretch: true,
                },
            },
            {
                name: 'server.sbox-packages',
                path: 'sbox-packages',
                component: () => import('~/views/server/sbox/pages/PackageBrowser.vue'),
                meta: {
                    icon: 'cube',
                    permission: 'startup.update',
                    feature: 'sbox-package-browser',
                    eggTag: 'sbox',
                    addon: 'physgun',
                },
                children: [
                    {
                        name: 'server.sbox-packages.show',
                        path: ':fullIdent',
                        component: () => import('~/views/server/sbox/pages/PackageDetail.vue'),
                        meta: {
                            icon: 'cube',
                            permission: 'startup.update',
                            feature: 'sbox-package-browser',
                            eggTag: 'sbox',
                            addon: 'physgun',
                            hidden: true,
                            noBreadcrumbs: true,
                        },
                    },
                ],
            },
            {
                name: 'server.management.files',
                path: 'files',
                component: () => import('~/views/Passthrough.vue'),
                redirect: { name: 'server.management.files.index' },
                meta: {
                    icon: 'folder-open',
                    permission: 'file.list',
                    passthrough: true,
                },
                children: [
                    {
                        name: 'server.management.files.index',
                        path: '',
                        component: () => import('~/views/server/files/FilesIndex.vue'),
                        meta: {
                            icon: 'folder-open',
                            permission: 'file.list',
                            stretch: true,
                        },
                    },
                    {
                        name: 'server.management.files.new',
                        path: 'new',
                        component: () => import('~/views/server/files/Manage.vue'),
                        meta: {
                            icon: 'file-alt',
                            permission: 'file.write',
                            stretch: true,
                        },
                    },
                    {
                        name: 'server.management.files.edit',
                        path: 'edit',
                        component: () => import('~/views/server/files/Manage.vue'),
                        meta: {
                            icon: 'file-alt',
                            permission: 'file.read',
                            stretch: true,
                        },
                    },
                ],
            },
            {
                name: 'server.configuration.palworld',
                path: 'palworld-server-config',
                component: () => import('~/views/server/configurators/palworld/Configurator.vue'),
                meta: {
                    icon: 'pen-to-square',
                    feature: 'configurator-palworld',
                    addon: 'physgun',
                },
            },
            {
                name: 'server.configuration.hytale',
                path: 'hytale-server-config',
                component: () => import('~/views/server/configurators/hytale/Configurator.vue'),
                meta: {
                    icon: 'pen-to-square',
                    // addon: 'physgun',
                    eggTag: 'hytale',
                },
            },
            {
                name: 'server.configuration.vrising',
                path: 'vrising-server-config',
                component: () => import('~/views/server/configurators/vrising/Configurator.vue'),
                meta: {
                    icon: 'pen-to-square',
                    feature: 'configurator-vrising',
                    addon: 'physgun',
                },
            },
            {
                name: 'server.configuration.rust_server_configurator',
                path: 'rust-server-config',
                component: () => import('~/views/server/configurators/rust/Configurator.vue'),
                meta: {
                    icon: 'pen-to-square',
                    permission: 'file.delete',
                    feature: 'configurator-rust',
                    addon: 'physgun',
                },
            },
            {
                name: 'server.configuration.minecraft_sever_properties',
                path: 'minecraft-server-config',
                component: () => import('~/views/server/configurators/minecraft/Configurator.vue'),
                meta: {
                    icon: 'pen-to-square',
                    permission: 'file.delete',
                    feature: 'configurator-minecraft',
                    addon: 'physgun',
                },
            },
            {
                name: 'server.configuration.gmod_server_configurator',
                path: 'gmod-server-config',
                component: () => import('~/views/server/configurators/garrysmod/Configurator.vue'),
                meta: {
                    icon: 'pen-to-square',
                    permission: 'file.delete',
                    feature: 'configurator-gmod',
                    addon: 'physgun',
                },
            },
            {
                name: 'server.configuration.startup',
                path: 'startup',
                component: () => import('~/views/server/startup/Index.vue'),
                meta: {
                    icon: 'sliders-h',
                    permission: 'startup.read',
                },
            },
            {
                name: 'server.system',
                path: '',
                component: () => import('~/views/Passthrough.vue'),
                meta: {
                    icon: 'computer',
                    showAsCategory: true,
                },
                children: [
                    {
                        name: 'server.system.details',
                        path: 'details',
                        component: () => import('~/views/server/details/Index.vue'),
                        meta: {
                            icon: 'chart-area',
                            permission: 'details.read',
                        },
                    },
                    {
                        name: 'server.system.sftp',
                        path: 'sftp',
                        component: () => import('~/views/server/sftp/Index.vue'),
                        meta: {
                            icon: 'file-upload',
                            permission: 'file.sftp',
                        },
                    },
                    {
                        name: 'server.system.ssh',
                        path: 'ssh',
                        component: () => import('~/views/server/ssh/Index.vue'),
                        meta: {
                            icon: 'code',
                            permission: 'server:ssh.connect',
                            feature: 'server:ssh',
                        },
                    },
                    {
                        name: 'server.system.audit_logs',
                        path: 'audit-logs',
                        component: () => import('~/views/server/audit-logs/Index.vue'),
                        meta: {
                            icon: 'fingerprint',
                            permission: 'server:audit.read',
                        },
                    },
                ],
            },
            {
                name: 'server.management',
                path: '',
                component: () => import('~/views/Passthrough.vue'),
                meta: {
                    icon: 'people-roof',
                    showAsCategory: true,
                },
                children: [
                    {
                        name: 'server.management.databases',
                        path: 'databases',
                        component: () => import('~/views/server/databases/Index.vue'),
                        meta: {
                            icon: 'database',
                            permission: 'database.read',
                        },
                    },
                    {
                        name: 'server.management.backups',
                        path: 'backups',
                        component: () => import('~/views/Passthrough.vue'),
                        redirect: { name: 'server.management.backups.index' },
                        meta: {
                            icon: 'boxes',
                            permission: 'backup.read',
                            passthrough: true,
                        },
                        children: [
                            {
                                name: 'server.management.backups.index',
                                path: '',
                                component: () => import('~/views/server/backups/Index.vue'),
                                meta: {
                                    icon: 'boxes',
                                    permission: 'backup.read',
                                },
                            },
                            {
                                name: 'server.management.backups.explore',
                                path: ':backup/explore',
                                component: () => import('~/views/server/backups/explore/BackupExplore.vue'),
                                meta: {
                                    icon: 'folder-tree',
                                    permission: ['backup.read', 'file.list', 'file.read'],
                                    feature: 'backup-explore',
                                    ln: 'v2',
                                    stretch: true,
                                    hidden: true,
                                },
                            },
                        ],
                    },
                    {
                        name: 'server.management.subusers.index',
                        path: 'subusers',
                        component: () => import('~/views/server/subusers/Index.vue'),
                        meta: {
                            icon: 'users',
                            permission: 'subuser.read',
                        },
                    },
                    {
                        name: 'server.management.allocations',
                        path: 'allocations',
                        component: () => import('~/views/server/allocations/Index.vue'),
                        meta: {
                            icon: 'network-wired',
                            permission: 'allocation.read',
                        },
                    },
                ],
            },
            {
                name: 'server.configuration',
                path: '',
                component: () => import('~/views/Passthrough.vue'),
                meta: {
                    icon: 'cog',
                    showAsCategory: true,
                },
                children: [
                    {
                        name: 'server.configuration.advanced',
                        path: 'advanced',
                        component: () => import('~/views/server/advanced/Index.vue'),
                        meta: {
                            icon: 'cogs',
                        },
                    },
                    {
                        name: 'server.configuration.disk_usage',
                        path: 'disk-analyzer',
                        component: () => import('~/views/server/disk-usage/Index.vue'),
                        meta: {
                            icon: 'chart-pie',
                            permission: 'file.list',
                            addon: 'physgun',
                            stretch: true,
                        },
                    },
                    {
                        name: 'server.configuration.git',
                        path: 'git',
                        component: () => import('~/views/server/git/GitOverview.vue'),
                        meta: {
                            icon: 'code-branch',
                            permission: 'file.git',
                            feature: 'server:git',
                        },
                    },
                    {
                        name: 'server.configuration.importer',
                        path: 'import',
                        component: () => import('~/views/server/importer/ImportWizard.vue'),
                        meta: {
                            icon: 'file-import',
                            feature: 'server:importer',
                        },
                    },
                    {
                        name: 'server.configuration.schedules',
                        path: 'schedules',
                        component: () => import('~/views/Passthrough.vue'),
                        redirect: { name: 'server.configuration.schedules.index' },
                        meta: {
                            icon: 'calendar-alt',
                            permission: 'schedule.read',
                        },
                        children: [
                            {
                                name: 'server.configuration.schedules.index',
                                path: '',
                                component: () => import('~/views/server/schedules/Index.vue'),
                                meta: {
                                    icon: 'calendar-alt',
                                    permission: 'schedule.read',
                                },
                            },
                            {
                                name: 'server.configuration.schedules.manage',
                                path: ':schedule',
                                component: () => import('~/views/server/schedules/Manage.vue'),
                                meta: {
                                    icon: 'calendar-alt',
                                },
                            },
                        ],
                    },
                ],
            },
            {
                name: 'server.tools',
                path: '',
                component: () => import('~/views/Passthrough.vue'),
                meta: {
                    icon: 'wrench',
                    showAsCategory: true,
                },
                children: [
                    {
                        name: 'server.tools.subdomains',
                        path: 'subdomains',
                        component: () => import('~/views/server/subdomains/Index.vue'),
                        meta: {
                            icon: 'cubes',
                            permission: 'subdomain.read',
                            feature: 'domain-manager',
                        },
                    },
                    {
                        name: 'server.tools.wipe',
                        path: 'wipe',
                        component: () => import('~/views/server/wipe/Index.vue'),
                        meta: {
                            eggTag: 'rust',
                            permission: 'server:rustwipe.trigger',
                            icon: 'cogs',
                        },
                    },
                    {
                        name: 'server.tools.modpacks',
                        path: 'modpacks',
                        component: () => import('~/views/server/modpacks/Index.vue'),
                        meta: {
                            icon: 'cubes',
                            permission: 'modpack.read',
                            feature: 'modpack-manager',
                        },
                    },
                    {
                        name: 'server.tools.plugins',
                        path: 'plugins',
                        component: () => import('~/views/server/plugins/Index.vue'),
                        meta: {
                            icon: 'cogs',
                            permission: 'game_mod.read',
                            feature: 'plugin-manager',
                            nestTag: 'minecraft',
                        },
                    },
                    {
                        name: 'server.tools.plugin-browser',
                        path: 'plugin-browser',
                        component: () => import('~/views/server/rust/pages/PluginBrowser.vue'),
                        meta: {
                            icon: 'plug',
                            feature: 'rust-plugin-browser',
                            eggTag: 'rust',
                        },
                    },
                    {
                        name: 'server.tools.hytale-plugin-browser',
                        path: 'hytale-plugin-browser',
                        component: () => import('~/views/server/hytale/pages/PluginBrowser.vue'),
                        meta: {
                            icon: 'plug',
                            eggTag: 'hytale',
                        },
                    },
                    {
                        name: 'server.tools.mods',
                        path: 'mods',
                        component: () => import('~/views/server/mods/Index.vue'),
                        meta: {
                            icon: 'plug',
                            permission: 'mod.read',
                            feature: 'mod-manager',
                        },
                    },
                    {
                        name: 'server.tools.backdoor_scanner',
                        path: 'backdoor-scanner',
                        component: () => import('~/views/server/backdoor-scanner/Index.vue'),
                        meta: {
                            icon: 'door-open',
                            permission: 'scanner.update',
                            feature: 'backdoor-scanner',
                        },
                    },
                    {
                        name: 'server.tools.loading_screen',
                        path: 'loading-screen',
                        component: () => import('~/views/server/loading-screen/Index.vue'),
                        meta: {
                            icon: 'sync',
                            feature: 'loading-screen',
                        },
                    },
                    {
                        name: 'server.tools.fastdl',
                        path: 'fastdl',
                        component: () => import('~/views/server/fastdl/Index.vue'),
                        meta: {
                            icon: 'download',
                            permission: 'fastdl.read',
                            feature: 'fastdl',
                        },
                    },
                ],
            },
            {
                name: 'server.help',
                path: '',
                component: () => import('~/views/Passthrough.vue'),
                meta: {
                    icon: 'life-ring',
                    showAsCategory: true,
                },
                children: [
                    {
                        name: 'server.help.discord',
                        path: 'discord',
                        component: () => import('~/views/ExternalRedirect.vue'),
                        meta: {
                            icon: 'fab-discord',
                            addon: 'physgun',
                            externalUrl: 'https://discord.gg/physgun',
                            fallbackRoute: { name: 'index' },
                        },
                    },
                    {
                        name: 'server.help.knowledgebase',
                        path: 'knowledgebase',
                        component: () => import('~/views/ExternalRedirect.vue'),
                        meta: {
                            icon: 'book',
                            addon: 'physgun',
                            externalUrl: 'https://help.physgun.com',
                            fallbackRoute: { name: 'index' },
                        },
                    },
                    {
                        name: 'server.help.ticket',
                        path: 'ticket',
                        component: () => import('~/views/ExternalRedirect.vue'),
                        meta: {
                            icon: 'ticket',
                            addon: 'physgun',
                            externalUrl: 'https://billing.physgun.com/submitticket.php',
                            fallbackRoute: { name: 'index' },
                        },
                    },
                ],
            },
            {
                name: 'server.gmodstore',
                path: 'gmodstore',
                component: () => import('~/views/ExternalRedirect.vue'),
                meta: {
                    icon: 'cart-shopping',
                    addon: 'physgun',
                    eggTag: 'garrysmod',
                    externalUrl: 'https://gmodstore.com?ref=physgun_gp',
                    fallbackRoute: { name: 'index' },
                },
            },
            {
                name: 'server.hire_a_developer',
                path: 'magic',
                component: () => import('~/views/ExternalRedirect.vue'),
                meta: {
                    icon: 'code',
                    eggTag: 'rust',
                    addon: 'physgun',
                    externalUrl: 'https://phs.gg/magic?ref=gcp',
                    fallbackRoute: { name: 'index' },
                },
            },
            {
                name: 'server.whmcs',
                path: 'whmcs',
                component: () => import('~/views/ExternalRedirect.vue'),
                meta: {
                    icon: 'file-invoice',
                    whmcsOnly: true,
                    addon: 'physgun',
                    externalUrl: 'whmcs',
                    fallbackRoute: { name: 'index' },
                },
            },
            {
                name: 'server.administrate',
                path: 'administrate',
                component: () => import('~/views/Passthrough.vue'),
                beforeEnter: () => {
                    const modelsStore = useModelsStore();
                    const server = modelsStore.server?.getSluggedRouteID();

                    if (server) {
                        window.location.assign(about.url(server));
                    }

                    return false;
                },
                meta: {
                    icon: 'shield-alt',
                    adminOnly: true,
                    permission: 'admin:server.read',
                    allowWhenInstalling: true,
                },
            },
            {
                name: 'server.whmcs.admin',
                path: 'whmcs',
                component: () => import('~/views/ExternalRedirect.vue'),
                meta: {
                    icon: 'shield',
                    adminOnly: true,
                    whmcsOnly: true,
                    externalUrl: 'whmcs-admin',
                    fallbackRoute: { name: 'index' },
                },
            },
        ],
    },
    {
        path: '/server/:server/console',
        component: () => import('~/views/PopupLayout.vue'),
        meta: {
            middlewares: [Authenticated, Require2FA, new ModelBindings('client')],
        },
        children: [
            {
                name: 'server.console',
                path: '',
                component: () => import('~/views/server/Console.vue'),
            },
        ],
    },
];
